import { Color } from '@thiememeulenhoff/tokens';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HasSlotController } from '@thiememeulenhoff/lit-sdk';
import { ContentModalComponent } from '../content/content.component';
import { styles, tag } from './confirmation.styles';

@customElement(tag)
export class ConfirmationModalComponent extends ContentModalComponent {
  static override styles = [...super.styles, styles];

  @property({ type: String, attribute: 'confirm-color' })
  confirmColor: Color = 'blue';

  @property({ type: String, attribute: 'confirm-label' })
  confirmLabel = 'Bevestigen';

  @property({ type: Boolean, attribute: 'confirm-disabled' })
  confirmDisabled = false;

  @property({ type: String, attribute: 'confirm-data-test' })
  confirmDataTest = 'modal.button.confirm';

  @property({ type: String, attribute: 'cancel-color' })
  cancelColor: Color = 'blue';

  @property({ type: String, attribute: 'cancel-label' })
  cancelLabel = 'Annuleren';

  @property({ type: String, attribute: 'cancel-data-test' })
  cancelDataTest = 'modal.button.cancel';

  @property({ type: String, attribute: 'sticky-footer' })
  stickyFooter = false;

  @property({ type: Boolean, attribute: 'custom-confirm', reflect: true })
  customConfirm = false;

  closing = false;

  #hasSlotController = new HasSlotController(this, 'header');

  override extraControls() {
    return html`<footer>
      <a-button
        color=${this.confirmColor}
        ?disabled=${this.confirmDisabled}
        data-test=${this.confirmDataTest}
        @click=${this.confirm}
      >
        ${this.confirmLabel}
      </a-button>
      <a-button
        color=${this.cancelColor}
        variant="ghost"
        data-test=${this.cancelDataTest}
        @click=${this.close}
      >
        ${this.cancelLabel}
      </a-button>
    </footer>`;
  }

  override dialogContent() {
    return html`<container
      part="container"
      class=${classMap({
        'has-header': this.title || this.#hasSlotController.test('header'),
      })}
    >
      ${this.header()}
      <main
        part="main"
        id="content"
        class="${this.stickyFooter ? 'scrollable' : ''}"
      >
        <slot></slot>
      </main>
      ${this.extraControls()} ${this.closeButton()}
    </container>`;
  }

  confirm() {
    if (this.closing) {
      return;
    }

    if (this.customConfirm) {
      this.dispatchGlobalEvent({
        eventName: 'modalConfirm',
      });

      return;
    }

    this.close('confirm');
  }

  override open() {
    super.open();
    this.closing = false;
  }

  override close(closeReason?: string) {
    this.closing = true;
    super.close(closeReason);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-confirmation-modal': ConfirmationModalComponent;
  }
}
